import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';

function clp(n) {
  return `$${Number(n || 0).toLocaleString('es-CL')}`;
}

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CL');
}

function estadoLabel(estado) {
  if (estado === 'pagado') return 'Pagado';
  if (estado === 'pendiente') return 'Pendiente';
  return '—';
}

export default function CobranzaHistorialDetallePage() {
  const { cid, periodo } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('unidades');

  const load = useCallback(() => {
    if (!cid || !periodo) return Promise.resolve();
    setError('');
    return api
      .get(`/gastos-comunes/condominios/${cid}/cobranza/historial`, { params: { periodo } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [cid, periodo]);

  useEffect(() => {
    localStorage.setItem('gc_condominio_id', cid);
    load();
  }, [cid, load]);

  const r = data?.resumen || {};

  return (
    <div className="page-historial-cobranza page-historial-detalle">
      <header className="ingresos-head">
        <div>
          <p className="page-lead page-lead-compact">
            <Link to={`/c/${cid}/cobranza/historial`}>← Historial por mes</Link>
          </p>
          <h1 className="page-title">{data?.periodo?.label || periodo}</h1>
          {data && (
            <p className="page-lead page-lead-compact">
              Período de servicio <strong>{data.periodo.label}</strong> · cobro en calendario{' '}
              <strong>{data.mesCalendarioCobro?.label}</strong> · estado{' '}
              <strong>{data.periodo.estado}</strong>
              {data.periodo.vencimientoBase ? (
                <>
                  {' '}
                  · vencimiento base {fmtFecha(data.periodo.vencimientoBase)}
                </>
              ) : null}
            </p>
          )}
        </div>
        <div className="ingresos-head-actions">
          <Link className="btn secondary" to={`/c/${cid}/informes?periodo=${periodo}`}>
            Informe comunitario
          </Link>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      {data && (
        <>
          <div className="historial-kpi-grid">
            <div className="historial-kpi">
              <span className="historial-kpi-label">Emitido</span>
              <strong>{clp(r.montoEmitido)}</strong>
            </div>
            <div className="historial-kpi">
              <span className="historial-kpi-label">Cobrado (imputado al período)</span>
              <strong>{clp(r.montoCobrado)}</strong>
            </div>
            <div className="historial-kpi">
              <span className="historial-kpi-label">Saldo pendiente hoy</span>
              <strong className={r.saldoPendiente > 0 ? 'celda-pendiente' : ''}>{clp(r.saldoPendiente)}</strong>
            </div>
            <div className="historial-kpi">
              <span className="historial-kpi-label">Unidades pagadas / con cargo</span>
              <strong>
                {r.unidadesPagadas ?? 0} / {r.unidadesConCargo ?? 0}
              </strong>
            </div>
            <div className="historial-kpi">
              <span className="historial-kpi-label">Ingresos caja (mes calendario cobro)</span>
              <strong>{clp(r.ingresosCajaMesCalendario)}</strong>
              <span className="historial-kpi-sub">{r.pagosRegistradosMesCalendario || 0} pagos</span>
            </div>
          </div>

          <div className="historial-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              className={tab === 'unidades' ? 'active' : ''}
              onClick={() => setTab('unidades')}
            >
              Unidades y cargos ({data.unidades?.length || 0})
            </button>
            <button
              type="button"
              role="tab"
              className={tab === 'pagos-periodo' ? 'active' : ''}
              onClick={() => setTab('pagos-periodo')}
            >
              Pagos imputados ({data.pagosImputadosAlPeriodo?.length || 0})
            </button>
            <button
              type="button"
              role="tab"
              className={tab === 'pagos-calendario' ? 'active' : ''}
              onClick={() => setTab('pagos-calendario')}
            >
              Pagos mes calendario ({data.pagosMesCalendario?.length || 0})
            </button>
          </div>

          {tab === 'unidades' && (
            <div className="historial-resumen-table-shell">
              <table className="historial-resumen-table historial-detalle-unidades">
                <thead>
                  <tr>
                    <th>Depto</th>
                    <th>Propietario</th>
                    <th className="num">Cargo</th>
                    <th className="num">Cobrado</th>
                    <th className="num">Saldo</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.unidades || []).map((u) => (
                    <tr key={u.unidadId}>
                      <td>{u.codigoDepto}</td>
                      <td>{u.titularNombre}</td>
                      <td className="num">{clp(u.bruto)}</td>
                      <td className="num">{clp(u.cobrado)}</td>
                      <td className={`num ${u.saldo > 0 ? 'celda-pendiente' : ''}`}>{clp(u.saldo)}</td>
                      <td>{estadoLabel(u.estado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'pagos-periodo' && (
            <div className="historial-resumen-table-shell">
              <p className="page-lead page-lead-compact">
                Pagos cuya imputación tocó cargos de este período de servicio (puede ser fecha distinta al mes
                calendario de cobro).
              </p>
              <table className="historial-resumen-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Folio</th>
                    <th>Depto</th>
                    <th>Propietario</th>
                    <th className="num">Pago total</th>
                    <th className="num">Imputado aquí</th>
                    <th>Medio</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.pagosImputadosAlPeriodo || []).map((p) => (
                    <tr key={p.pagoId}>
                      <td>{fmtFecha(p.fechaPago)}</td>
                      <td>{p.folioNum ?? '—'}</td>
                      <td>{p.codigoDepto}</td>
                      <td>{p.titularNombre}</td>
                      <td className="num">{clp(p.importe)}</td>
                      <td className="num">{clp(p.imputadoEstePeriodo)}</td>
                      <td>{p.medio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.pagosImputadosAlPeriodo?.length && (
                <p className="empty-hint">Sin pagos imputados a cargos de este período.</p>
              )}
            </div>
          )}

          {tab === 'pagos-calendario' && (
            <div className="historial-resumen-table-shell">
              <p className="page-lead page-lead-compact">
                Todos los pagos confirmados registrados en <strong>{data.mesCalendarioCobro?.label}</strong>{' '}
                (incluye abonos a deudas de otros meses).
              </p>
              <table className="historial-resumen-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Folio</th>
                    <th>Depto</th>
                    <th>Propietario</th>
                    <th className="num">Monto</th>
                    <th>Medio</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.pagosMesCalendario || []).map((p) => (
                    <tr key={p.pagoId}>
                      <td>{fmtFecha(p.fechaPago)}</td>
                      <td>{p.folioNum ?? '—'}</td>
                      <td>{p.codigoDepto}</td>
                      <td>{p.titularNombre}</td>
                      <td className="num">{clp(p.importe)}</td>
                      <td>{p.medio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!data.pagosMesCalendario?.length && (
                <p className="empty-hint">Sin pagos en ese mes calendario.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
